import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sparkles, TrendingUp, Clock, Edit3 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface BioTopicsDisplayCardProps {
  topics: string[];
  bioScore?: number;
  lastAnalyzed?: string;
  onEditBio: () => void;
  isEditMode?: boolean;
}

export const BioTopicsDisplayCard: React.FC<BioTopicsDisplayCardProps> = ({
  topics,
  bioScore,
  lastAnalyzed,
  onEditBio,
  isEditMode = false
}) => {
  const hasTopics = topics && topics.length > 0;
  const MAX_PREVIEW_TOPICS = 5;
  const maxTopics = isEditMode ? topics.length : MAX_PREVIEW_TOPICS;
  const visibleTopics = hasTopics ? topics.slice(0, maxTopics) : [];
  const remainingCount = hasTopics && topics.length > maxTopics 
    ? topics.length - maxTopics 
    : 0;

  const getScoreColor = (score?: number) => {
    if (!score) return 'text-muted-foreground';
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-orange-400';
  };

  const getScoreLabel = (score?: number) => {
    if (!score) return 'Não analisada';
    if (score >= 80) return 'Excelente';
    if (score >= 60) return 'Boa';
    return 'Regular';
  };

  // Helper function to generate feedback based on score
  const getFeedbackMessage = (score?: number, topicsCount: number = 0) => {
    if (!score || topicsCount === 0) {
      return {
        title: 'Adicione uma bio para começar',
        message: 'Uma bio bem escrita ajuda nossa IA a entender seus interesses e melhorar seus matches.',
        type: 'empty' as const,
        color: 'text-muted-foreground'
      };
    }
    
    if (score >= 80) {
      return {
        title: 'Bio excelente! 🎉',
        message: `Sua bio está muito bem escrita e nossa IA identificou ${topicsCount} tópicos sobre você. Isso vai melhorar significativamente seus matches.`,
        type: 'excellent' as const,
        color: 'text-green-400'
      };
    }
    
    if (score >= 60) {
      return {
        title: 'Bio boa, mas pode melhorar',
        message: `Nossa IA identificou ${topicsCount} tópicos. Para melhorar ainda mais, adicione mais detalhes sobre suas paixões, hobbies ou o que você busca.`,
        type: 'good' as const,
        color: 'text-yellow-400'
      };
    }
    
    return {
      title: 'Sua bio precisa de atenção',
      message: `Nossa IA identificou apenas ${topicsCount} tópicos. Adicione mais informações sobre você, seus interesses, hobbies e o que te define para melhorar seus matches.`,
      type: 'needs-improvement' as const,
      color: 'text-orange-400'
    };
  };

  // Preview Mode: Show qualitative feedback
  if (!isEditMode && hasTopics) {
    const feedback = getFeedbackMessage(bioScore, topics.length);
    
    return (
      <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
        <CardContent className="p-6 space-y-4">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-900/30 border border-purple-500/30">
              <Sparkles className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                Qualidade da Bio
              </h3>
            </div>
          </div>

          {/* Feedback Content */}
          <div className="space-y-3">
            <h4 className={`text-base font-medium ${feedback.color}`}>
              {feedback.title}
            </h4>
            
            <p className="text-sm text-white/90 leading-relaxed">
              {feedback.message}
            </p>

            {/* Compact Stats */}
            <div className="flex items-center gap-3 text-xs text-muted-foreground pt-2 border-t border-white/5">
              {bioScore && (
                <span className={`flex items-center gap-1 ${feedback.color}`}>
                  <TrendingUp className="w-3 h-3" />
                  {bioScore}/100 • {getScoreLabel(bioScore)}
                </span>
              )}
              {lastAnalyzed && (
                <span className="flex items-center gap-1 text-white/90">
                  <Clock className="w-3 h-3" />
                  há {formatDistanceToNow(new Date(lastAnalyzed), { locale: ptBR })}
                </span>
              )}
            </div>
          </div>

          {/* Edit Button */}
          <Button
            onClick={onEditBio}
            variant="outline"
            className="w-full border-purple-500/30 hover:bg-purple-900/20"
          >
            <Edit3 className="w-4 h-4 mr-2" />
            {!bioScore ? 'Adicionar Bio' : 'Editar Bio'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Edit Mode or Default: Show full management interface
  return (
    <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
      <CardContent className="p-4 sm:p-6 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-purple-900/30 border border-purple-500/30">
              <Sparkles className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                {isEditMode ? `Gerenciar Tópicos (${topics.length})` : 'Tópicos da Bio'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isEditMode ? 'Todos os tópicos extraídos' : 'Gerados automaticamente por IA'}
              </p>
            </div>
          </div>
          {!isEditMode && hasTopics && (
            <Button
              onClick={onEditBio}
              size="sm"
              variant="outline"
              className="border-purple-500/30 hover:bg-purple-900/20 hover:border-purple-500/50"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              Editar Bio
            </Button>
          )}
        </div>

        {/* Content */}
        {hasTopics ? (
          <div className="space-y-4">
            {/* Description */}
            {!isEditMode && (
              <p className="text-sm text-muted-foreground">
                Nossa IA detectou <strong className="text-purple-400">{topics.length} tópicos</strong> sobre você baseados na sua bio. 
                Estes tópicos são usados para encontrar pessoas compatíveis.
              </p>
            )}

            {/* Topics */}
            <div className="flex flex-wrap gap-2 max-h-[300px] overflow-y-auto">
              {visibleTopics.map((topic, index) => (
                <Badge
                  key={`${topic}-${index}`}
                  variant="outline"
                  className="bg-purple-900/40 border-purple-500/40 text-purple-200 hover:bg-purple-900/60 transition-colors"
                >
                  {topic}
                </Badge>
              ))}
              {remainingCount > 0 && (
                <Badge
                  variant="outline"
                  className="bg-purple-900/20 border-purple-500/30 text-purple-300"
                >
                  e mais {remainingCount}
                </Badge>
              )}
            </div>

            {/* Stats */}
            {!isEditMode && (
              <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-white/10">
                {/* Quality Score */}
                <div className="flex items-center gap-2">
                  <TrendingUp className={`w-4 h-4 ${getScoreColor(bioScore)}`} />
                  <span className="text-sm text-muted-foreground">
                    Qualidade:
                  </span>
                  <span className={`text-sm font-medium ${getScoreColor(bioScore)}`}>
                    {bioScore ? `${bioScore}/100` : 'N/A'} ({getScoreLabel(bioScore)})
                  </span>
                </div>

                {/* Last Analyzed */}
                {lastAnalyzed && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Última análise: há {formatDistanceToNow(new Date(lastAnalyzed), { locale: ptBR })}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-6 space-y-3">
            <div className="w-12 h-12 mx-auto rounded-full bg-purple-900/20 border border-purple-500/30 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-purple-400" />
            </div>
            <div className="space-y-2">
              <p className="text-white font-medium">
                Sua bio ainda está vazia
              </p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Adicione uma bio para que nossa IA identifique seus interesses e melhore seus matches.
              </p>
            </div>
            <Button
              onClick={onEditBio}
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              Adicionar Bio
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
